/**
 * Configuration API
 * Fetches compiled configurations via the Connect API
 */

import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import type { ConnectAuthStage } from '@/lib/connect-api';
import { getCompiledConfigFromConnect, getPublicConfigFromConnect } from '@/lib/connect-api';
import { mapLanguageToLocale } from '@/lib/footer-links';
import { isGlobalSurface } from '@/lib/surface-mode';
import type { JourneyConfiguration, PaymentMethodProviders, PaymentPlanTier } from '@/types';

// Environment-based configuration scope for pre-auth branding
const CONFIG_SCOPE = import.meta.env.VITE_CONFIG_SCOPE as 'client' | 'operator' | undefined;
const CONFIG_SCOPE_ID = import.meta.env.VITE_CONFIG_SCOPE_ID as string | undefined;

/**
 * Fetch authenticated configuration for a given scope and ID
 *
 * @param scope - Configuration scope (client, operator, or account)
 * @param scopeId - Entity ID for the scope
 * @param getAccessToken - Optional function to get JWT token (for authenticated requests)
 */
async function fetchConfigurationByScope(
  scope: 'client' | 'operator' | 'account',
  scopeId: string,
  getAccessToken?: () => Promise<string | null>
): Promise<JourneyConfiguration | null> {
  try {
    const authStage: ConnectAuthStage =
      (localStorage.getItem(STORAGE_KEYS.AUTH_STAGE) as 'limited' | 'full' | null) ?? 'limited';
    const target =
      scope === 'client'
        ? { clientId: scopeId }
        : scope === 'operator'
          ? { operatorId: scopeId }
          : { accountId: scopeId };

    const [themeConfig, contentConfig, journeyConfig] = await Promise.all([
      getCompiledConfigFromConnect(target, 'theme', authStage, getAccessToken),
      getCompiledConfigFromConnect(target, 'content', authStage, getAccessToken),
      getCompiledConfigFromConnect(target, 'journey', authStage, getAccessToken),
    ]);

    return transformToJourneyConfiguration(journeyConfig?.config_json, themeConfig?.config_json, contentConfig?.config_json);
  } catch {
    return null;
  }
}

/**
 * Fetch pre-auth configuration based on environment variables
 * Called on every app boot to show correct client/operator branding
 *
 * Uses GET /connect/v1/config/public — the unauthenticated boot route, which
 * only serves 'theme' and 'content'. Journey (payment settings, fees) is
 * account-scoped and fetched post-auth via /config/compiled.
 */
export async function fetchPreAuthConfiguration(): Promise<JourneyConfiguration | null> {
  if (isGlobalSurface()) return null;
  if (!CONFIG_SCOPE || !CONFIG_SCOPE_ID) {
    return null;
  }

  try {
    const target = CONFIG_SCOPE === 'client' ? { clientId: CONFIG_SCOPE_ID } : { operatorId: CONFIG_SCOPE_ID };
    const [themeConfig, contentConfig] = await Promise.all([
      getPublicConfigFromConnect(target, 'theme'),
      getPublicConfigFromConnect(target, 'content'),
    ]);

    return transformToJourneyConfiguration(null, themeConfig?.config_json, contentConfig?.config_json);
  } catch {
    return null;
  }
}

/**
 * Fetch compiled configuration for an account via the Connect API
 * (GET /connect/v1/config/compiled), which handles hierarchical merging server-side.
 *
 * @param accountId - Account ID (e.g., 'acct_123')
 * @param getAccessToken - Optional function to get JWT token for authenticated requests
 * @returns JourneyConfiguration merged from journey and theme domains, or null if fetch fails
 */
export async function fetchAccountConfiguration(
  accountId: string,
  getAccessToken?: () => Promise<string | null>
): Promise<JourneyConfiguration | null> {
  return fetchConfigurationByScope('account', accountId, getAccessToken);
}

// Bacs defaults closed: unlike the other methods, a config that predates the
// `bacs` key means no operator has wired a Bacs provider yet, so offering it
// anyway routes to checkout with nothing configured to serve it.
const DEFAULT_PROVIDERS: PaymentMethodProviders = {
  card: { enabled: true },
  sepa: { enabled: true },
  multibanco: { enabled: true },
  mb_way: { enabled: true },
  bacs: { enabled: false },
};

export function mapProviders(raw: unknown): PaymentMethodProviders {
  if (!raw || typeof raw !== 'object') return DEFAULT_PROVIDERS;
  const p = raw as Record<string, unknown>;
  const getEnabled = (key: string, defaultValue = true): boolean => {
    const val = p[key];
    if (val && typeof val === 'object') {
      return ((val as Record<string, unknown>).enabled as boolean | undefined) ?? defaultValue;
    }
    return defaultValue;
  };
  return {
    card: { enabled: getEnabled('card') },
    sepa: { enabled: getEnabled('sepa') },
    multibanco: { enabled: getEnabled('multibanco') },
    mb_way: { enabled: getEnabled('mb_way') },
    bacs: { enabled: getEnabled('bacs', false) },
  };
}

/**
 * Transform database config JSONs into JourneyConfiguration format
 *
 * @param journeyConfigJson - Config JSON from 'journey' domain (contains payment settings)
 * @param themeConfigJson - Config JSON from 'theme' domain (contains UI/branding settings)
 * @param contentConfigJson - Config JSON from 'content' domain (contains footer links, company info)
 * @returns JourneyConfiguration or null if all configs are empty
 */
export function transformToJourneyConfiguration(
  journeyConfigJson: unknown,
  themeConfigJson: unknown,
  contentConfigJson?: unknown
): JourneyConfiguration | null {
  // If all configs are empty/null, return null to use defaults
  if (!journeyConfigJson && !themeConfigJson && !contentConfigJson) {
    return null;
  }

  const config: Partial<JourneyConfiguration> = {};

  // Transform journey domain config (payment settings)
  if (journeyConfigJson && typeof journeyConfigJson === 'object') {
    const journey = journeyConfigJson as Record<string, unknown>;

    // Map payment_settings from journey domain
    if (journey.payment_settings && typeof journey.payment_settings === 'object') {
      const paymentSettings = journey.payment_settings as Record<string, unknown>;
      const frequencies = Array.isArray(paymentSettings.paymentFrequencies)
        ? (paymentSettings.paymentFrequencies as ('monthly' | 'bi-weekly' | 'weekly')[])
        : ['monthly' as const];

      config.payment = {
        allowPartialPayment: (paymentSettings.allowPartialPayment as boolean | undefined) ?? true,
        partialPaymentTab: (paymentSettings.partialPaymentTab as boolean | undefined) ?? true,
        allowAccountPartialPayment: (paymentSettings.allowAccountPartialPayment as boolean | undefined) ?? false,
        allowFullPayLater: (paymentSettings.allowFullPayLater as boolean | undefined) ?? true,
        allowPartialPayLater: (paymentSettings.allowPartialPayLater as boolean | undefined) ?? true,
        enablePaymentPlan: (paymentSettings.enablePaymentPlan as boolean | undefined) ?? true,
        planPaymentMethods: (paymentSettings.planPaymentMethods as string[] | undefined) ?? ['card'],
        providers: mapProviders(paymentSettings.providers),
        maxDaysInFuture: (paymentSettings.maxDaysInFuture as number | undefined) ?? 30,
        minDebtForPartialPayment: (paymentSettings.minDebtForPartialPayment as number | undefined) ?? 10,
        minPartialPaymentAmount: (paymentSettings.minPartialPaymentAmount as number | undefined) ?? 0,
        paymentPlan: {
          paymentFrequencies: frequencies,
        },
      };
    }

    // Map payment_plan_tiers if present (at root level of journey config, not nested in payment_settings)
    if (Array.isArray(journey.payment_plan_tiers) && journey.payment_plan_tiers.length > 0) {
      config.payment = config.payment || {
        allowPartialPayment: true,
        partialPaymentTab: true,
        allowAccountPartialPayment: false,
        allowFullPayLater: true,
        allowPartialPayLater: true,
        enablePaymentPlan: true,
        planPaymentMethods: ['card'],
        providers: DEFAULT_PROVIDERS,
        maxDaysInFuture: 30,
        minDebtForPartialPayment: 10,
        minPartialPaymentAmount: 0,
      };
      const existingFrequencies = config.payment.paymentPlan?.paymentFrequencies ?? ['monthly' as const];
      config.payment.paymentPlan = {
        paymentFrequencies: existingFrequencies,
        tiers: journey.payment_plan_tiers
          .map((tier: unknown): PaymentPlanTier | null => {
            if (typeof tier === 'object' && tier !== null) {
              const t = tier as Record<string, unknown>;
              const result: PaymentPlanTier = {
                id: (t.id as string) || `tier_${t.minimumAmount || 0}`,
                debtThreshold: (t.minimumAmount as number) || 0,
                maxDurationMonths: (t.maxInstallments as number) || 12,
              };
              if (typeof t.minimumPaymentAmount === 'number') {
                result.minInstallmentAmount = t.minimumPaymentAmount;
              }
              if (typeof t.name === 'string') {
                result.label = t.name;
              }
              return result;
            }
            return null;
          })
          .filter((tier): tier is PaymentPlanTier => tier !== null),
      };
    }

    // Map authentication configuration from journey domain
    if (journey.authentication && typeof journey.authentication === 'object') {
      const authConfig = journey.authentication as Record<string, unknown>;
      config.authentication = {
        authMode: (authConfig.authMode as 'early_verification' | 'deferred_verification' | 'none') || 'none',
        allowGuestCheckout: authConfig.allowGuestCheckout as boolean | undefined,
        twoFactorMethods: Array.isArray(authConfig.twoFactorMethods)
          ? (authConfig.twoFactorMethods as ('email' | 'sms')[])
          : undefined,
        restrictedPages: Array.isArray(authConfig.restrictedPages)
          ? (authConfig.restrictedPages as string[])
          : undefined,
        requireVerificationForPayments: authConfig.requireVerificationForPayments as boolean | undefined,
        requireVerificationForPlans: authConfig.requireVerificationForPlans as boolean | undefined,
      };
    }
  }

  // Transform theme domain config (UI/branding settings)
  if (themeConfigJson && typeof themeConfigJson === 'object') {
    const theme = themeConfigJson as Record<string, unknown>;
    const colors = theme.colors as Record<string, unknown> | undefined;

    const features = theme.features as Record<string, unknown> | undefined;
    config.ui = {
      branding: {
        theme: (theme.theme as 'professional' | 'approachable') || 'professional',
        colors: {
          // Map 'brand' from schema to 'primary' in customer-portal
          primary: (colors?.brand as string) || (colors?.primary as string) || '#7f53dd',
          primaryForeground: colors?.primaryForeground as string | undefined,
          buttonBackground: colors?.buttonBackground as { light?: string; dark?: string } | undefined,
          buttonText: colors?.buttonText as { light?: string; dark?: string } | undefined,
        },
        logo:
          theme.logo_full && typeof theme.logo_full === 'object'
            ? {
                // Use light or dark based on what's available, or use single URL if provided as string
                url: (theme.logo_full as { light?: string })?.light || (theme.logo_full as { dark?: string })?.dark,
                light: (theme.logo_full as { light?: string })?.light,
                dark: (theme.logo_full as { dark?: string })?.dark,
                alt: theme.logo_alt as string | undefined,
              }
            : undefined,
        logoIcon:
          theme.logo_icon && typeof theme.logo_icon === 'object'
            ? {
                light: (theme.logo_icon as { light?: string })?.light,
                dark: (theme.logo_icon as { dark?: string })?.dark,
              }
            : undefined,
        showGradientSphere: (theme.showGradientSphere as boolean | undefined) ?? undefined,
        favicon:
          theme.favicon && typeof theme.favicon === 'object'
            ? {
                // Use light or dark favicon, or single URL if provided as string
                url: (theme.favicon as { light?: string })?.light || (theme.favicon as { dark?: string })?.dark || '',
              }
            : undefined,
      },
      // Map defaultLanguage from theme schema to language in customer-portal.
      // Theme stores short ISO codes (`fr`); the portal's LanguageContext keys on
      // BCP 47 tags (`fr-FR`), so normalise here or the saved default is dropped.
      // The final fallback matches theme.schema.json's declared default ('en') —
      // an unconfigured market must not inherit a Portuguese default.
      language: mapLanguageToLocale((theme.defaultLanguage as string) || (theme.language as string) || 'en'),
      // Map availableLanguages from theme schema (moved from journey domain)
      availableLanguages: Array.isArray(theme.availableLanguages)
        ? (theme.availableLanguages as string[]).map(mapLanguageToLocale)
        : ['en-GB'],
      features: {
        // Map features from theme schema (moved from journey.ui_features)
        enableChat: (features?.enableChat as boolean) ?? false,
        showMergeAccounts: (features?.showMergeAccounts as boolean) ?? true,
        showPaymentHistory: (features?.showPaymentHistory as boolean) ?? true,
        showDisputes: (features?.showDisputes as boolean) ?? true,
        showDocuments: (features?.showDocuments as boolean) ?? true,
        showAccountBalance: (features?.showAccountBalance as boolean) ?? true,
      },
      termsLinks: Array.isArray(theme.termsLinks) ? (theme.termsLinks as Array<{ name: string; url: string }>) : undefined,
    };
  }

  // Transform content domain config (footer links, company info)
  if (contentConfigJson && typeof contentConfigJson === 'object') {
    const content = contentConfigJson as Record<string, unknown>;

    // Map footer_links from content domain to termsLinks in UI config
    if (Array.isArray(content.footer_links) && content.footer_links.length > 0) {
      // Initialize UI config if it doesn't exist
      if (!config.ui) {
        config.ui = {
          branding: {
            theme: 'professional',
            colors: {
              primary: '#7f53dd',
            },
          },
          language: 'en-GB',
          availableLanguages: ['en-GB'],
          features: {
            enableChat: false,
            showMergeAccounts: true,
            showPaymentHistory: true,
            showDisputes: true,
            showDocuments: true,
            showAccountBalance: true,
          },
        };
      }

      // Transform footer_links to termsLinks format
      // Content domain links can have localized names (object) or simple strings
      // Preserve the localized format so the Footer component can handle localization based on current language
      const transformedLinks = content.footer_links.map((link: unknown) => {
        if (typeof link === 'object' && link !== null) {
          const l = link as Record<string, unknown>;
          const url = typeof l.url === 'string' ? l.url : '#';

          // Preserve localized name object as-is, or use string directly
          // The Footer component will handle locale selection via getLocalizedFooterLinks()
          let name: string | Record<string, string>;
          if (typeof l.name === 'string') {
            name = l.name;
          } else if (l.name && typeof l.name === 'object') {
            // Preserve the localized object for runtime locale selection
            name = l.name as Record<string, string>;
          } else {
            name = 'Link';
          }

          return { name, url };
        }
        return { name: 'Link' as string | Record<string, string>, url: '#' };
      });

      // Content domain footer_links override theme domain termsLinks if present
      config.ui.termsLinks = transformedLinks;
    }

    // Map company_info for company details, support info, and legal URLs
    if (content.company_info && typeof content.company_info === 'object') {
      const companyInfo = content.company_info as Record<string, unknown>;

      // Initialize UI config if it doesn't exist
      if (!config.ui) {
        config.ui = {
          branding: {
            theme: 'professional',
            colors: {
              primary: '#7f53dd',
            },
          },
          language: 'en-GB',
          availableLanguages: ['en-GB'],
          features: {
            enableChat: false,
            showMergeAccounts: true,
            showPaymentHistory: true,
            showDisputes: true,
            showDocuments: true,
            showAccountBalance: true,
          },
        };
      }

      // Map all company_info fields
      config.ui.companyInfo = {
        companyName: typeof companyInfo.company_name === 'string' ? companyInfo.company_name : undefined,
        companyPhone: typeof companyInfo.company_phone === 'string' ? companyInfo.company_phone : undefined,
        companyEmail: typeof companyInfo.company_email === 'string' ? companyInfo.company_email : undefined,
        companyAddress: typeof companyInfo.company_address === 'string' ? companyInfo.company_address : undefined,
        fcaFrn: typeof companyInfo.fca_frn === 'string' ? companyInfo.fca_frn : undefined,
        supportUrl: typeof companyInfo.support_url === 'string' ? companyInfo.support_url : undefined,
        supportEmail: typeof companyInfo.support_email === 'string' ? companyInfo.support_email : undefined,
        supportPhone: typeof companyInfo.support_phone === 'string' ? companyInfo.support_phone : undefined,
        supportAddress: typeof companyInfo.support_address === 'string' ? companyInfo.support_address : undefined,
        termsUrl: typeof companyInfo.terms_url === 'string' ? companyInfo.terms_url : undefined,
        privacyPolicyUrl: typeof companyInfo.privacy_policy_url === 'string' ? companyInfo.privacy_policy_url : undefined,
        unsubscribeUrlTemplate:
          typeof companyInfo.unsubscribe_url_template === 'string' ? companyInfo.unsubscribe_url_template : undefined,
      };

      // Also set legacy companyName for backward compatibility
      if (config.ui.companyInfo.companyName) {
        config.ui.companyName = config.ui.companyInfo.companyName;
      }
    }
  }

  // Return null if no config was found (all domains empty)
  // This signals to use defaults
  if (!config.payment && !config.ui && !config.authentication) {
    return null;
  }

  // Return partial config - will be merged with defaults in ConfigurationContext
  return config as JourneyConfiguration;
}
